import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Loader2, BookOpen, Tag } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LangSwitcher from "@/components/LangSwitcher";
import { listBlog, getBlogTopics, getMeta, getStoredLang, setStoredLang } from "@/lib/api";

const CAT_ACCENT = {
    know_your_rights: { label: "KNOW YOUR RIGHTS", color: "text-white" },
    in_the_moment: { label: "IN THE MOMENT", color: "text-[#FF3B30]" },
    fight_back: { label: "FIGHT BACK", color: "text-[#FFCC00]" },
    diy_pro_se: { label: "DIY PRO SE", color: "text-white" },
};

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [topics, setTopics] = useState({ categories: [], topics: [] });
    const [meta, setMeta] = useState(null);
    const [activeCat, setActiveCat] = useState("");
    const [lang, setLang] = useState(getStoredLang());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        getBlogTopics().then(setTopics).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        listBlog(activeCat || undefined, undefined, lang)
            .then((r) => setPosts(r.posts || []))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, [activeCat, lang]);

    const onLangChange = (l) => {
        setStoredLang(l);
        setLang(l);
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
                        LEGAL EDUCATION · FREE · WRITTEN FOR HUMANS
                    </div>
                    <h1
                        className="font-anton uppercase leading-[0.85] text-5xl sm:text-7xl md:text-8xl"
                        data-testid="blog-headline"
                    >
                        The law,<br />
                        in <span className="text-[#FF3B30]">plain English</span>.
                    </h1>
                    <p className="mt-8 max-w-2xl font-sans text-white/70 text-base sm:text-lg leading-relaxed">
                        Step-by-step answers for the moments the system tells you to "consult an
                        attorney." Each article names specific statutes, forms, agencies, and free
                        resources you can use today.
                    </p>
                </div>
            </section>

            {/* CATEGORY FILTER */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-6 grid grid-cols-2 md:grid-cols-5 gap-0 border-2 border-white/20 mt-6" data-testid="blog-categories">
                    <button
                        onClick={() => setActiveCat("")}
                        className={`p-4 border-white/20 [&:not(:last-child)]:border-r-2 transition-colors text-left ${activeCat === "" ? "bg-white text-black" : "hover:bg-white/5"}`}
                        data-testid="cat-all"
                    >
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-60">
                            FILTER
                        </div>
                        <div className="font-anton text-lg uppercase mt-1">ALL POSTS</div>
                    </button>
                    {(topics.categories || []).map((c) => {
                        const active = activeCat === c.key;
                        return (
                            <button
                                key={c.key}
                                onClick={() => setActiveCat(c.key)}
                                data-testid={`cat-${c.key}`}
                                className={`p-4 border-white/20 [&:not(:last-child)]:border-r-2 transition-colors text-left ${active ? "bg-white text-black" : "hover:bg-white/5"}`}
                            >
                                <div className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-60">
                                    CATEGORY
                                </div>
                                <div className="font-anton text-lg uppercase mt-1">{c.label}</div>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* POSTS */}
            <section className="border-b-2 border-white/20">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    {loading && (
                        <div className="p-16 text-center">
                            <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mt-4">
                                LOADING...
                            </div>
                        </div>
                    )}

                    {!loading && posts.length === 0 && (
                        <div className="border-2 border-dashed border-white/20 p-16 text-center">
                            <BookOpen size={32} className="text-white/30 mx-auto mb-4" />
                            <div className="font-anton text-3xl uppercase">Articles brewing.</div>
                            <p className="font-sans text-white/60 mt-3">
                                The blog auto-publishes — refresh in a few moments.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-white/20" data-testid="blog-posts">
                        {posts.map((p, i) => {
                            const cat = CAT_ACCENT[p.category] || CAT_ACCENT.know_your_rights;
                            return (
                                <Link
                                    key={p.post_id}
                                    to={`/hpm-blog/${p.slug}`}
                                    data-testid={`blog-card-${i}`}
                                    className="block p-6 border-white/20 lg:[&:not(:nth-child(3n))]:border-r-2 md:odd:border-r-2 [&:not(:last-child)]:border-b-2 hover:bg-white/5 transition-colors"
                                >
                                    <div className={`font-mono text-[10px] uppercase tracking-[0.3em] mb-3 ${cat.color}`}>
                                        {cat.label}
                                    </div>
                                    <h2 className="font-anton text-2xl uppercase leading-tight">
                                        {p.title}
                                    </h2>
                                    {p.subtitle && (
                                        <p className="font-sans text-sm text-white/70 mt-3 leading-relaxed">
                                            {p.subtitle}
                                        </p>
                                    )}
                                    <div className="mt-5 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                        <span className="flex items-center gap-1">
                                            <Clock size={10} /> {p.reading_time_minutes} MIN
                                        </span>
                                        {p.tags?.slice(0, 2).map((t, j) => (
                                            <span key={j} className="flex items-center gap-1">
                                                <Tag size={10} /> {t}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] flex items-center gap-1">
                                        READ <ArrowRight size={12} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
